'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import CaseStudyCards from '@/app/newlp/CaseStudyCards'
import FaqFooter from '@/components/marketing/FaqFooter'
import WhitelabelCTA from '@/components/services/WhitelabelCTA'
import auditFaqs from '@/data/audit-faqs.json'
import {
  SMART_AUDITS_CHANNEL_DIRECTORY,
  SMART_AUDITS_CRM,
  SMART_AUDITS_CRM_DIRECTORY,
  SMART_AUDITS_DIRECTORY,
  SMART_AUDITS_FIT,
  SMART_AUDITS_INCLUDED,
  SMART_AUDITS_HERO,
  SMART_AUDITS_HERO_CASES,
  SMART_AUDITS_MARKETING,
  type DirectoryItem,
  type SmartAudit,
} from '@/lib/smart-audits-data'
import { trackPostHogEvent } from '@/lib/utils/dataLayer'

function cardDomId(id: string) {
  return `audit-card-${id}`
}

function trackCta(text: string, location: string) {
  trackPostHogEvent('cta_click', {
    cta_text: text,
    cta_location: location,
    cta_type: 'secondary',
  })
}

function goCardCta(href: string, text: string, location: string) {
  trackCta(text, location)
  if (!href.startsWith('#')) return
  document.getElementById(href.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function DirectoryRow({
  item,
  selected,
  onSelect,
}: {
  item: DirectoryItem
  selected: boolean
  onSelect: (id: string) => void
}) {
  return (
    <a
      className={`mrow${selected ? ' on' : ''}`}
      href="#selected-audit"
      aria-expanded={selected}
      onClick={(event) => {
        event.preventDefault()
        onSelect(item.id)
      }}
    >
      {item.logos?.length ? (
        <span className="chips">
          {item.logos.map((logo) => (
            <span className="chip" key={`${item.chip}-${logo.src}`}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={logo.src} alt="" />
            </span>
          ))}
        </span>
      ) : null}
      {item.chip}
    </a>
  )
}

function CheckMark() {
  return (
    <span className="ck" aria-hidden>
      <svg viewBox="0 0 24 24" fill="none">
        <path
          d="M20 6.5 9.25 17.25 4 12"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

function SampleEmailForm({ auditName }: { auditName: string }) {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const trimmed = email.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus('error')
      return
    }
    setStatus('sending')
    try {
      const res = await fetch('/api/form-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: trimmed.split('@')[0],
          email: trimmed,
          focusArea: `Requested a free demo sample of ${auditName}`,
          serviceName: auditName,
          source: 'audit-lp-demo-sample',
        }),
      })
      if (!res.ok) {
        setStatus('error')
        return
      }
      trackPostHogEvent('form_submitted', {
        form_source: 'audit-lp-demo-sample',
        service_name: auditName,
      })
      setStatus('sent')
      setEmail('')
    } catch {
      setStatus('error')
    }
  }

  if (status === 'sent') {
    return <p className="cSampleOk">{SMART_AUDITS_INCLUDED.sampleSuccess}</p>
  }

  return (
    <form className="cSample" onSubmit={submit}>
      <p className="cSampleLb">{SMART_AUDITS_INCLUDED.sampleLabel}</p>
      <div className="cSampleRow">
        <input
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => {
            setEmail(event.target.value)
            if (status === 'error') setStatus('idle')
          }}
          placeholder={SMART_AUDITS_INCLUDED.samplePlaceholder}
          aria-label={SMART_AUDITS_INCLUDED.samplePlaceholder}
        />
        <button type="submit" disabled={status === 'sending'}>
          {status === 'sending' ? 'Sending…' : SMART_AUDITS_INCLUDED.sampleCta}
        </button>
      </div>
      {status === 'error' ? <p className="cSampleErr">Enter a valid email and try again.</p> : null}
    </form>
  )
}

function Step2Detail({ audit }: { audit: SmartAudit }) {
  return (
    <div className="cDetail" id={cardDomId(audit.id)} aria-live="polite">
      <div className="cMain">
        <h3 className="cTitle">{audit.name}</h3>
        <p className="cDesc">{audit.blurb}</p>
        <div className="cFacts">
          <div>
            <em>Fixed price</em>
            <strong>{audit.price}</strong>
          </div>
          <div>
            <em>Turnaround</em>
            <strong>{audit.days}</strong>
          </div>
          <div>
            <em>{SMART_AUDITS_INCLUDED.creditLabel}</em>
            <strong>{SMART_AUDITS_INCLUDED.credit}</strong>
          </div>
        </div>
        <Link
          className="btn cBtn"
          href={audit.href}
          onClick={(event) => {
            if (audit.href.startsWith('#')) event.preventDefault()
            goCardCta(audit.href, audit.cta, 'audit_step2')
          }}
        >
          {audit.cta}
        </Link>
      </div>
      <aside className="cGet">
        <h4>{SMART_AUDITS_INCLUDED.getTitle}</h4>
        <ul>
          {SMART_AUDITS_INCLUDED.getItems.map((item) => (
            <li key={item}>
              <CheckMark />
              {item}
            </li>
          ))}
        </ul>
        <SampleEmailForm auditName={audit.name} />
      </aside>
    </div>
  )
}

export default function SmartAuditsPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null)

  useEffect(() => {
    if (!selectedId) return
    document.getElementById('selected-audit')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [selectedId])

  const selectAudit = (id: string) => {
    setSelectedId(id)
  }

  const selectedAudit =
    SMART_AUDITS_CRM.find((audit) => audit.id === selectedId) ??
    SMART_AUDITS_MARKETING.find((audit) => audit.id === selectedId) ??
    null

  return (
    <>
      <div className="audit-hero bg-white lg:px-16">
        <CaseStudyCards
          ctaHref={SMART_AUDITS_HERO.primaryHref}
          bgSrc="/images/homepage/new-hero.avif"
          bgOverlay
          badgeLabel=""
          tags={[]}
          cases={SMART_AUDITS_HERO_CASES}
          heading={SMART_AUDITS_HERO.title}
          headingEm={SMART_AUDITS_HERO.titleEm}
          sub={SMART_AUDITS_HERO.sub}
          ctaLabel={SMART_AUDITS_HERO.primaryCta}
          trustHref="#testimonials"
          paths={[{ label: SMART_AUDITS_HERO.secondaryCta, href: SMART_AUDITS_HERO.secondaryHref }]}
        />
      </div>

      <div className="smart-audits bg-white">
        <div className="menuwrap">
          <div className="flow">
            <div className={`flow-step${selectedId ? ' done' : ' on'}`}>
              <div className="flow-rail" aria-hidden>
                <span className="n">1</span>
                <span className="line" />
              </div>
              <div className="flow-body">
                <div className="flow-hd">
                  <h3>{SMART_AUDITS_DIRECTORY.title}</h3>
                  <p className="credit">{SMART_AUDITS_DIRECTORY.credit}</p>
                </div>
                <nav className="menu" id="menu" aria-label="Audits directory">
                  <div className="mgrp crm">
                    <section className="fit" aria-labelledby="audit-fit-title">
                      <h2 id="audit-fit-title">{SMART_AUDITS_FIT.title}</h2>
                      <ul className="fit-grid">
                        {SMART_AUDITS_FIT.items.map((item) => (
                          <li key={item}>
                            <CheckMark />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </section>
                    <div className="mrow-head">
                      <div className="mcat">CRM &amp; systems</div>
                      <div className="mitems">
                        {SMART_AUDITS_CRM_DIRECTORY.map((item) => (
                          <DirectoryRow
                            key={item.id}
                            item={item}
                            selected={selectedId === item.id}
                            onSelect={selectAudit}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="mgrp">
                    <div className="mcat">Marketing &amp; tracking</div>
                    <div className="mitems">
                      {SMART_AUDITS_CHANNEL_DIRECTORY.map((item) => (
                        <DirectoryRow
                          key={item.id}
                          item={item}
                          selected={selectedId === item.id}
                          onSelect={selectAudit}
                        />
                      ))}
                    </div>
                  </div>
                </nav>
              </div>
            </div>

            <div className={`flow-step${selectedId ? ' on' : ' wait'}`} id="selected-audit">
              <div className="flow-rail" aria-hidden>
                <span className="n">2</span>
              </div>
              <div className="flow-body">
                {selectedAudit ? (
                  <Step2Detail audit={selectedAudit} />
                ) : (
                  <div className="flow-hd">
                    <h3>Request this audit</h3>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <WhitelabelCTA
        heading="Request an audit"
        buttonText="Request an audit"
        formSource="audit-lp"
        serviceName="Smart Audits"
        focusAreaPlaceholder="CRM, tracking, or ads - not sure which audit we need. Google Ads + HubSpot, numbers don't match."
      />

      <FaqFooter
        title={
          <>
           Questions clients
           ask first.
          </>
        }
        faqs={auditFaqs}
      />
    </>
  )
}
